import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Plus, Truck, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  VehicleBookingDialog,
  bookingLabel,
  formatBookingDates,
  formatBookingDrivers,
} from "@/components/vehicles/VehicleBookingDialog";
import type { ProjectWithRelations } from "@/hooks/useProjects";
import {
  useProjectVehicleBookings,
  useVehicleBookingsInRange,
  type VehicleBooking,
} from "@/hooks/useVehicles";
import { formatDate } from "@/lib/date-utils";
import { findBookingConflicts } from "@/lib/vehicle-bookings";

/** Vehicle bookings of a project, each with its own period and driver. */
export function ProjectVehiclesTab({
  project,
}: {
  project: ProjectWithRelations;
}) {
  const { data: bookings, isLoading } = useProjectVehicleBookings(project.id);
  const [dialog, setDialog] = useState<{
    open: boolean;
    booking: VehicleBooking | null;
  }>({ open: false, booking: null });

  // All bookings in the period of this project's bookings, to find conflicts
  const range = useMemo(() => {
    if (bookings.length === 0) return null;
    return {
      from: Math.min(...bookings.map((booking) => booking.startDate)),
      to: Math.max(...bookings.map((booking) => booking.endDate)),
    };
  }, [bookings]);
  const { data: rangeBookings } = useVehicleBookingsInRange(
    range?.from ?? 0,
    range?.to ?? 0,
    range !== null,
  );
  const conflicts = useMemo(
    () => findBookingConflicts(rangeBookings),
    [rangeBookings],
  );

  // New bookings default to the project's planning or event period
  const defaults = useMemo(() => {
    const start =
      project.planning_start_date ??
      project.event_start_date ??
      project.start_date ??
      undefined;
    const end =
      project.planning_end_date ??
      project.event_end_date ??
      project.end_date ??
      start;
    return { startDate: start ?? undefined, endDate: end ?? undefined };
  }, [project]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Vehicles ({bookings.length})</h3>
        <Button
          offlineEdit
          onClick={() => setDialog({ open: true, booking: null })}
          data-testid="book-vehicle-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Book vehicle
        </Button>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">
          Loading vehicles...
        </p>
      ) : bookings.length === 0 ? (
        <div className="rounded-lg border py-8 text-center text-muted-foreground">
          No vehicles booked for this project yet.
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map((booking) => {
            const vehicle = booking.vehicle;
            const photo = vehicle?.images?.[0]?.imageURL;
            const others = conflicts.get(booking.id) ?? [];
            return (
              <Card
                key={booking.id}
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => setDialog({ open: true, booking })}
                data-testid={`project-vehicle-booking-${booking.id}`}
              >
                <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4">
                  {photo ? (
                    <img
                      src={photo}
                      alt=""
                      loading="lazy"
                      className="h-12 w-12 max-w-none rounded-md border object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-muted">
                      <Truck className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {vehicle ? (
                        <Link
                          to={`/vehicles/${vehicle.id}?tab=bookings`}
                          className="hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {vehicle.name}
                        </Link>
                      ) : (
                        "Vehicle"
                      )}
                      {vehicle?.licensePlate && (
                        <span className="ml-2 font-mono text-sm text-muted-foreground">
                          {vehicle.licensePlate}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatBookingDates(booking)}
                      {booking.outboundDate && booking.returnDate && (
                        <span
                          data-testid={`project-vehicle-travel-${booking.id}`}
                        >
                          {" "}
                          · Outbound{" "}
                          {formatDate(new Date(booking.outboundDate))}, return{" "}
                          {formatDate(new Date(booking.returnDate))}
                        </span>
                      )}
                    </p>
                    {booking.notes && (
                      <p className="whitespace-pre-wrap text-sm">
                        {booking.notes}
                      </p>
                    )}
                    {others.length > 0 && (
                      <p
                        className="mt-1 flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400"
                        data-testid={`project-vehicle-conflict-${booking.id}`}
                      >
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Also booked:{" "}
                        {others
                          .map(
                            (other) =>
                              `${bookingLabel(other)} (${formatBookingDates(other)})`,
                          )
                          .join(", ")}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className="shrink-0 gap-1">
                    <UserRound className="h-3 w-3" />
                    {formatBookingDrivers(booking) ?? "No driver"}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <VehicleBookingDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        booking={dialog.booking}
        defaults={defaults}
        fixedProjectId={project.id}
      />
    </div>
  );
}
